import { useCallback, useEffect, useState } from 'react';
import { ethers } from 'ethers';
import MicrojobPaymentABI from 'abis/MicrojobPayment';
import { ensureCorrectChain } from './vaultChain.utils';
import { fetchDaoOperators, isDaoOrganizerWallet } from './vaultRoles.utils';

export const isValidMicrojobAddress = (addr) => /^0x[0-9a-fA-F]{40}$/.test((addr || '').trim());

export async function loadMicrojobRoles(microjobPaymentAddress, chainId, walletAddress) {
  const empty = {
    walletAddress: walletAddress || '',
    isMicrojobAdmin: false,
    isMicrojobOperator: false,
    operatorRows: [],
    usdtTokenAddress: '',
  };

  if (!isValidMicrojobAddress(microjobPaymentAddress) || !window.ethereum) {
    return empty;
  }

  await ensureCorrectChain(chainId);
  const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
  const contract = new ethers.Contract(microjobPaymentAddress.trim(), MicrojobPaymentABI, provider);

  const [adminRole, operatorRole] = await Promise.all([
    contract.ADMIN_ROLE(),
    contract.DAO_OPERATOR_ROLE(),
  ]);

  let isMicrojobAdmin = false;
  let isMicrojobOperator = false;
  if (walletAddress) {
    [isMicrojobAdmin, isMicrojobOperator] = await Promise.all([
      contract.hasRole(adminRole, walletAddress),
      contract.hasRole(operatorRole, walletAddress),
    ]);
  }

  const onChainOps = await fetchDaoOperators(contract);
  const operatorRows = onChainOps
    .map((address) => ({ address }))
    .sort((a, b) => a.address.localeCompare(b.address));

  let usdtTokenAddress = '';
  try {
    const count = await contract.getCurrencyCount();
    if (count && Number(count) > 0) {
      usdtTokenAddress = await contract.currencyAddresses(0);
    }
  } catch {
    usdtTokenAddress = '';
  }

  return {
    walletAddress: walletAddress || '',
    isMicrojobAdmin,
    isMicrojobOperator,
    operatorRows,
    usdtTokenAddress,
  };
}

/**
 * Roles for Microjob tab/configuration — reads MicrojobPayment only (not Vault).
 */
export function useMicrojobRoles(microjobPaymentAddress, chainId, organizerAddress) {
  const [walletAddress, setWalletAddress] = useState('');
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [roles, setRoles] = useState({
    isMicrojobAdmin: false,
    isMicrojobOperator: false,
    operatorRows: [],
    usdtTokenAddress: '',
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!window.ethereum) {
      setWalletAddress('');
      setIsOrganizer(false);
      setRoles({
        isMicrojobAdmin: false,
        isMicrojobOperator: false,
        operatorRows: [],
        usdtTokenAddress: '',
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const accounts = await provider.listAccounts();
      const account = accounts[0] || '';
      setWalletAddress(account);
      setIsOrganizer(isDaoOrganizerWallet(account, organizerAddress));

      if (!isValidMicrojobAddress(microjobPaymentAddress)) {
        setRoles({
          isMicrojobAdmin: false,
          isMicrojobOperator: false,
          operatorRows: [],
          usdtTokenAddress: '',
        });
        return;
      }

      const next = await loadMicrojobRoles(microjobPaymentAddress, chainId, account);
      setRoles({
        isMicrojobAdmin: next.isMicrojobAdmin,
        isMicrojobOperator: next.isMicrojobOperator,
        operatorRows: next.operatorRows,
        usdtTokenAddress: next.usdtTokenAddress,
      });
    } catch {
      setRoles({
        isMicrojobAdmin: false,
        isMicrojobOperator: false,
        operatorRows: [],
        usdtTokenAddress: '',
      });
    } finally {
      setLoading(false);
    }
  }, [microjobPaymentAddress, chainId, organizerAddress]);

  useEffect(() => {
    refresh();
    if (!window.ethereum) return undefined;
    const onChange = () => refresh();
    window.ethereum.on('accountsChanged', onChange);
    window.ethereum.on('chainChanged', onChange);
    return () => {
      if (window.ethereum?.removeListener) {
        window.ethereum.removeListener('accountsChanged', onChange);
        window.ethereum.removeListener('chainChanged', onChange);
      }
    };
  }, [refresh]);

  const hasMicrojobConfigured = isValidMicrojobAddress(microjobPaymentAddress);
  const canAccessMicrojobTab = isOrganizer || roles.isMicrojobAdmin || roles.isMicrojobOperator;

  return {
    walletAddress,
    isOrganizer,
    loading,
    refresh,
    hasMicrojobConfigured,
    canAccessMicrojobTab,
    ...roles,
  };
}
